import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ALAIN_I18N_TOKEN, I18nPipe } from '@delon/theme';
import { NzAlertModule } from 'ng-zorro-antd/alert';
import { NzButtonModule } from 'ng-zorro-antd/button';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { NzIconModule } from 'ng-zorro-antd/icon';
import { NzInputModule } from 'ng-zorro-antd/input';
import { NzSelectModule } from 'ng-zorro-antd/select';
import { NzSpinModule } from 'ng-zorro-antd/spin';
import { NzTableModule } from 'ng-zorro-antd/table';
import { NzTagModule } from 'ng-zorro-antd/tag';
import { finalize } from 'rxjs';
import { I18NService } from 'src/app/core/i18n/i18n.service';
import { HttpService } from 'src/app/http/http.service';
import { TestIdDirective } from 'src/app/shared/directives/test-id.directive';
import { ConstantUtil } from 'src/app/util/constant.util';

interface GcpLogFilters {
  startTime: Date;
  endTime: Date;
  serviceName: string;
  severity: string;
  traceId: string;
  podName: string;
  keyword: string;
}

@Component({
  selector: 'app-gcp-log-list',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    NzAlertModule,
    NzButtonModule,
    NzDatePickerModule,
    NzIconModule,
    NzInputModule,
    NzSelectModule,
    NzSpinModule,
    NzTableModule,
    NzTagModule,
    I18nPipe,
    TestIdDirective
  ],
  templateUrl: './gcp-log-list.component.html',
  styleUrls: ['./gcp-log-list.component.less'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class GcpLogListComponent {
  private readonly i18n = inject<I18NService>(ALAIN_I18N_TOKEN);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly http = inject(HttpService);

  readonly maxRangeMilliseconds = 60 * 60 * 1000;
  readonly severityOptions = ['DEFAULT', 'DEBUG', 'INFO', 'NOTICE', 'WARNING', 'ERROR', 'CRITICAL', 'ALERT', 'EMERGENCY'];
  filters: GcpLogFilters = this.createDefaultFilters();
  items: any[] = [];
  loading = false;
  hasSearched = false;
  errorMessage = '';
  nextPageToken = '';
  private pageToken = '';
  private readonly pageTokenHistory: string[] = [];
  selectedLog: any | null = null;
  queryCollapsed = false;

  get pageTokenHistoryLength(): number {
    return this.pageTokenHistory.length;
  }

  getSeverityClass(severity: string): string {
    switch (String(severity || '').toUpperCase()) {
      case 'ERROR':
      case 'CRITICAL':
      case 'ALERT':
      case 'EMERGENCY':
        return 'severity-error';
      case 'WARNING':
        return 'severity-warning';
      case 'NOTICE':
        return 'severity-notice';
      case 'INFO':
        return 'severity-info';
      case 'DEBUG':
        return 'severity-debug';
      default:
        return 'severity-default';
    }
  }

  getMessagePreview(message: unknown): string {
    const value = String(message ?? '').trim();
    return value.length > 120 ? `${value.slice(0, 119)}…` : value || '-';
  }

  openDetails(item: any): void {
    this.selectedLog = item;
    this.cdr.markForCheck();
  }

  closeDetails(): void {
    this.selectedLog = null;
    this.cdr.markForCheck();
  }

  toggleQuery(): void {
    this.queryCollapsed = !this.queryCollapsed;
    this.cdr.markForCheck();
  }

  getFilterSummary(): string {
    const active = [this.filters.serviceName, this.filters.severity, this.filters.traceId, this.filters.podName, this.filters.keyword]
      .map(value => String(value || '').trim())
      .filter(Boolean);
    return active.length ? active.join(' · ') : '5 min';
  }

  /**
   * 以目前條件查詢 GCP 日誌；Project 與 GKE 資源範圍由後端環境配置決定。
   */
  loadData(): void {
    this.errorMessage = '';
    if (!this.isValidTimeRange()) {
      this.errorMessage = 'log.gcp.time-range-too-large';
      this.cdr.markForCheck();
      return;
    }

    this.loading = true;
    this.http
      .post(ConstantUtil.ADMIN_GCP_LOG_LIST, this.buildRequest())
      .pipe(
        finalize(() => {
          this.loading = false;
          this.cdr.markForCheck();
        })
      )
      .subscribe({
        next: (res: any) => {
          if (!res?.success) {
            this.errorMessage = res?.message || 'log.gcp.query-failed';
            this.items = [];
            this.nextPageToken = '';
            return;
          }
          this.items = Array.isArray(res.data?.items) ? res.data.items : [];
          this.nextPageToken = res.data?.nextPageToken || '';
          this.hasSearched = true;
        },
        error: () => {
          this.errorMessage = 'log.gcp.query-failed';
          this.items = [];
          this.nextPageToken = '';
        }
      });
  }

  /**
   * 以新的條件重新查詢，清除舊游標避免跨條件翻頁。
   */
  search(): void {
    this.pageToken = '';
    this.pageTokenHistory.length = 0;
    this.loadData();
  }

  /**
   * 取下一頁 GCP 日誌，保留目前游標以支援返回上一頁。
   */
  loadNextPage(): void {
    if (!this.nextPageToken || this.loading) return;
    this.pageTokenHistory.push(this.pageToken);
    this.pageToken = this.nextPageToken;
    this.loadData();
  }

  /**
   * 返回上一頁 GCP 日誌；後端游標不支援反向查詢，因此由前端保存已使用游標。
   */
  loadPreviousPage(): void {
    if (!this.pageTokenHistory.length || this.loading) return;
    this.pageToken = this.pageTokenHistory.pop() || '';
    this.loadData();
  }

  /**
   * 還原最近五分鐘的預設查詢條件。
   */
  resetFilters(): void {
    this.filters = this.createDefaultFilters();
    this.items = [];
    this.nextPageToken = '';
    this.pageToken = '';
    this.pageTokenHistory.length = 0;
    this.errorMessage = '';
    this.hasSearched = false;
    this.search();
  }

  /**
   * 同步日期范围元件的值，避免模板直接修改数组中的 Date 物件。
   */
  onTimeRangeChange(range: Date[]): void {
    if (!Array.isArray(range) || range.length !== 2) return;
    this.filters.startTime = range[0];
    this.filters.endTime = range[1];
  }

  /**
   * 判斷查詢時間是否存在且不超過後端允許的一小時。
   */
  private isValidTimeRange(): boolean {
    const start = this.filters.startTime?.getTime();
    const end = this.filters.endTime?.getTime();
    return Number.isFinite(start) && Number.isFinite(end) && end >= start && end - start <= this.maxRangeMilliseconds;
  }

  /**
   * 組裝後端 GCP 日誌查詢 DTO，移除未填寫的可選欄位。
   */
  private buildRequest(): Record<string, unknown> {
    const request: Record<string, unknown> = {
      startTime: this.filters.startTime.toISOString(),
      endTime: this.filters.endTime.toISOString(),
      pageSize: 50
    };
    const optionalFields = ['serviceName', 'severity', 'traceId', 'podName', 'keyword'];
    optionalFields.forEach(field => {
      const value = this.filters[field as keyof GcpLogFilters];
      if (typeof value === 'string' && value.trim()) request[field] = value.trim();
    });
    if (this.pageToken) request['pageToken'] = this.pageToken;
    return request;
  }

  /**
   * 建立預設最近五分鐘的查詢條件。
   */
  private createDefaultFilters(): GcpLogFilters {
    const endTime = new Date();
    return {
      startTime: new Date(endTime.getTime() - 5 * 60 * 1000),
      endTime,
      serviceName: '',
      severity: '',
      traceId: '',
      podName: '',
      keyword: ''
    };
  }

  /**
   * 將後端錯誤碼轉成可翻譯的提示鍵。
   */
  getErrorMessage(): string {
    if (this.errorMessage === 'PERMISSION_DENIED') return this.i18n.fanyi('log.gcp.permission-denied');
    return this.i18n.fanyi(this.errorMessage);
  }
}
